import React from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import { useAuthenticatedFilePreview } from '../../hooks/useAuthenticatedFilePreview';
import type { MitarbeiterProfilDaten, ProfilAbschlussEintrag, ProfilFaehigkeitEintrag } from '../../types';

const ABS_ART_LABEL: Record<string, string> = {
  studium: 'Studium',
  ausbildung: 'Ausbildung',
  zertifikat: 'Zertifikat',
  sonstiges: 'Sonstiges',
};

const NIVEAU_LABEL: Record<string, string> = {
  grundkenntnisse: 'Grundkenntnisse',
  fortgeschritten: 'Fortgeschritten',
  expertise: 'Expertise',
};

function SectionHeader({ title, oeffentlich }: { title: string; oeffentlich: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      <Chip
        size="small"
        label={oeffentlich ? 'Für Kollegen sichtbar' : 'Privat'}
        color={oeffentlich ? 'success' : 'default'}
        variant="outlined"
      />
    </Box>
  );
}

function Cell({ label, value }: { label: string; value: React.ReactNode }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {empty ? '—' : value}
      </Typography>
    </Grid>
  );
}

export interface MitarbeiterProfilReadOnlyCardProps {
  profil: MitarbeiterProfilDaten;
}

export const MitarbeiterProfilReadOnlyCard: React.FC<MitarbeiterProfilReadOnlyCardProps> = ({ profil }) => {
  const avatarUrl = useAuthenticatedFilePreview(profil.avatar_file_id);

  const fmtAbschluss = (a: ProfilAbschlussEintrag) =>
    [
      a.bezeichnung,
      a.art ? `(${ABS_ART_LABEL[a.art] ?? a.art})` : '',
      a.institution ? `· ${a.institution}` : '',
      a.jahr != null ? `· ${a.jahr}` : '',
    ]
      .filter(Boolean)
      .join(' ');

  const fmtFaehigkeit = (f: ProfilFaehigkeitEintrag) =>
    [f.name, f.niveau ? `· ${NIVEAU_LABEL[f.niveau] ?? f.niveau}` : ''].filter(Boolean).join(' ');

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <BadgeIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Nutzerprofil
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Vom Mitarbeiter unter „Mein Profil“ gepflegt. Es werden nur Daten angezeigt, die er oder sie
          im jeweiligen Block für Kolleginnen und Kollegen freigegeben hat – dieselbe Sichtbarkeit wie im
          Kurzprofil. Private Inhalte sind auch für Administrationsrollen nicht einsehbar.
        </Typography>

        <SectionHeader title="Profilbild" oeffentlich={profil.profil_oeffentlich_avatar} />
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Avatar src={avatarUrl} sx={{ width: 72, height: 72 }}>
            {!avatarUrl && <PersonIcon fontSize="large" />}
          </Avatar>
          <Typography variant="body2" color="text.secondary">
            {profil.avatar_file_id ? `Datei-ID ${profil.avatar_file_id}` : 'Kein Bild hochgeladen'}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <SectionHeader title="Kontakt" oeffentlich={profil.profil_oeffentlich_kontakt} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Cell label="Handynummer" value={profil.handynummer} />
          <Cell label="Festnetz / Durchwahl" value={profil.festnetz} />
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              Kontaktzeitfenster
            </Typography>
            <Typography variant="body2">{profil.kontakt_zeitfenster || '—'}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <SectionHeader title="Adresse" oeffentlich={profil.profil_oeffentlich_adresse} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              Straße / Zusatz
            </Typography>
            <Typography variant="body2">{profil.adresse_zeile1 || '—'}</Typography>
          </Grid>
          <Cell label="PLZ" value={profil.plz} />
          <Cell label="Ort" value={profil.ort} />
          <Cell label="Land" value={profil.land} />
        </Grid>

        <Divider sx={{ my: 2 }} />

        <SectionHeader title="Erreichbarkeit" oeffentlich={profil.profil_oeffentlich_erreichbarkeit} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Cell label="Zeitzone" value={profil.zeitzone} />
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              Sprachen
            </Typography>
            <Typography variant="body2">
              {profil.sprachen?.length ? profil.sprachen.join(', ') : '—'}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <SectionHeader title="Berufliches" oeffentlich={profil.profil_oeffentlich_beruflich} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Cell label="Beruflicher Titel" value={profil.beruflicher_titel} />
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              LinkedIn
            </Typography>
            {profil.link_linkedin ? (
              <MuiLink href={profil.link_linkedin} target="_blank" rel="noopener noreferrer" variant="body2">
                {profil.link_linkedin}
              </MuiLink>
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              Website
            </Typography>
            {profil.link_website ? (
              <MuiLink href={profil.link_website} target="_blank" rel="noopener noreferrer" variant="body2">
                {profil.link_website}
              </MuiLink>
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <SectionHeader title="Kompetenzen" oeffentlich={profil.profil_oeffentlich_kompetenzen} />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Abschlüsse und Qualifikationen
        </Typography>
        {profil.profil_abschluesse?.length ? (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            {profil.profil_abschluesse.map((a, i) => (
              <Typography key={i} variant="body2">
                {fmtAbschluss(a)}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ mb: 2 }}>
            —
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Fähigkeiten
        </Typography>
        {profil.profil_faehigkeiten?.length ? (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            {profil.profil_faehigkeiten.map((f, i) => (
              <Typography key={i} variant="body2">
                {fmtFaehigkeit(f)}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ mb: 2 }}>
            —
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          Persönliches
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Geburtsdatum und Freitext haben jeweils einen eigenen Sichtbarkeitsschalter.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              Geburtsdatum
            </Typography>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="body2">{profil.geburtsdatum || '—'}</Typography>
              <Chip
                size="small"
                label={profil.profil_oeffentlich_geburtsdatum ? 'Geburtstag: für Kollegen sichtbar' : 'Geburtstag: privat'}
                color={profil.profil_oeffentlich_geburtsdatum ? 'success' : 'default'}
                variant="outlined"
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              Über mich
            </Typography>
            <Stack direction="row" alignItems="flex-start" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
              <Chip
                size="small"
                label={profil.profil_oeffentlich_beschreibung ? 'Freitext: für Kollegen sichtbar' : 'Freitext: privat'}
                color={profil.profil_oeffentlich_beschreibung ? 'success' : 'default'}
                variant="outlined"
              />
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {profil.profil_beschreibung || '—'}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
